import React, { useState, useRef, useEffect } from 'react';
import './ClientEsriCredentials.css';
import PortalHeader from '../clientNavbar/PortalHeader';
import { Link } from 'react-router-dom';
import { useAuth } from '../../Auth';

// Icons
import { RiSortAlphabetAsc, RiSortAlphabetDesc } from "react-icons/ri";
import { IoClose } from "react-icons/io5";

// Assets
import arrowDown from '../client-dashboard-assets/arrow-down.png'

// Interface for getting the ESRI users
interface EsriUsers {
    esri_username: string,
    esri_name: string,
    esri_email: string,
    esri_access: string,
}

interface User {
    name: string;
    email: string;
    username: string;
    organization: string;
    access: string;
}

const ClientEsriCredentials = () => {

    // Fetching detector images
    const [esriUsers, setEsriUsers] = useState<EsriUsers[]>([]);
    const { organization, username } = useAuth();

    const fetchEsriUsers = async () => {
        try {
            const response = await fetch('/api/esri_credentials/', {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data: EsriUsers[] = await response.json();
            setEsriUsers(data); // Assign the fetched gallery data to state
            // console.log(data)

        } catch (err) {
            // Type assertion to 'Error' to access the message property
            if (err instanceof Error) {
                console.error(err.message); // Handle error
            } else {
                console.error('An unknown error occurred'); // Fallback for unknown errors
            }
        }
    };

    useEffect(() => {
        fetchEsriUsers();
    },
        []); // Empty dependency array to run once on mount


    const matchedEsriUser = esriUsers.find(user => user.esri_username === username);


    const [esriFilter, setEsriFilter] = useState("organization");
    const [sortConfig, setSortConfig] = useState<{ key: keyof User, direction: 'asc' | 'desc' }>({
        key: 'name',
        direction: 'asc',
    });
    const [searchTerm, setSearchTerm] = useState('');

    const users: User[] = esriUsers.map(user => ({
        name: user.esri_name || '',
        email: user.esri_email || '',
        username: user.esri_username || '',
        organization: organization, // or resolve to org name if needed
        access: user.esri_access || '',
    }));


    // Filtering and sorting logic
    const filteredUsers = users
        .filter(user => user.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => {
            if (!sortConfig) return 0;
            const aKey = a[sortConfig.key].toLowerCase();
            const bKey = b[sortConfig.key].toLowerCase();

            if (aKey < bKey) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aKey > bKey) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

    const handleSort = (key: keyof User) => {
        setSortConfig(prev => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    // For mobile view
    const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
    const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);


    const toggleExpand = (index: number) => {
        setExpandedIndices(prev => {
            const isExpanding = !prev.includes(index);
            const newExpanded = isExpanding ? [...prev, index] : prev.filter(i => i !== index);

            if (isExpanding && rowRefs.current[index]) {
                setTimeout(() => {
                    const element = rowRefs.current[index];
                    const scrollContainer = document.querySelector('.esri-user-table-container');

                    if (element && scrollContainer) {
                        const elementTop = element.offsetTop;
                        const offset = 60; // Optional padding from top

                        scrollContainer.scrollTo({
                            top: elementTop - offset,
                            behavior: 'smooth'
                        });
                    }
                }, 100);
            }

            return newExpanded;
        });
    };



    return (
        <div className='esri-credentials-wrapper'>

            <PortalHeader />

            <h1>Esri Credentials


                <Link className='faq-adjust-buttons' style={{ textDecoration: 'none', color: 'black' }} to="/fnn_portal_esri" >
                    <button><strong>Back</strong></button>
                </Link>

            </h1>

            <div className='esri-filter-and-username'>

                <div className='esri-credentials-filter'>
                    {/* <p
                        onClick={() => setEsriFilter('account')}
                        className={esriFilter === 'account' ? 'active-filter' : ''}
                    >
                        Account Details
                    </p> */}
                    <p
                        onClick={() => setEsriFilter('organization')}
                        className={esriFilter === 'organization' ? 'active-filter' : ''}
                    >
                        Organization Information
                    </p>
                </div>

                {esriFilter === "account" && matchedEsriUser && 
                    <ul className='esri-user-info'>

                        <div>
                            <h2>Email Address</h2>
                            <li>{matchedEsriUser.esri_email}</li>
                        </div>

                        <div>
                            <h2>Username</h2>
                            <li>{matchedEsriUser.esri_username}</li>
                        </div>

                        <div>
                            <h2>Organization</h2>
                            <li>{organization}</li>
                        </div>

                        <div>
                            <h2>Access</h2>
                            <li>{matchedEsriUser.esri_access}</li>
                        </div>


                    </ul>
                }

                {esriFilter === "organization" && (
                    <div className="esri-search-container">
                        <input
                            className="esri-search-input"
                            type="text"
                            placeholder="Search users by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            id='esri-name-search'
                        />

                        <IoClose
                            className="clear-icon"
                            onClick={() => setSearchTerm('')}
                        />

                    </div>
                )}


            </div>

            {esriFilter === "organization" && (
                <div className="esri-user-table-container">
                    <table className="esri-user-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort("name")}>
                                    <span className="header-content sortable">
                                        <span className="header-label">Name</span>
                                        {sortConfig?.key === 'name' && sortConfig.direction === 'desc' ? (
                                            <RiSortAlphabetDesc />
                                        ) : (
                                            <RiSortAlphabetAsc />
                                        )}
                                    </span>
                                </th>
                                <th className="desktop-only"><span className="header-content">Email</span></th>
                                <th className="desktop-only"><span className="header-content">Username</span></th>
                                <th className="desktop-only"><span className="header-content">Organization</span></th>
                                <th className="desktop-only"><span className="header-content">Access</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user, index) => (
                                <React.Fragment key={index}>
                                    <tr
                                        onClick={() => toggleExpand(index)}
                                        className="user-row"
                                        ref={el => rowRefs.current[index] = el}
                                    >
                                        <td>
                                            {user.name}
                                            <span className={`dropdown-icon ${expandedIndices.includes(index) ? 'rotate' : ''}`}>
                                                <img src={arrowDown} alt='drop-down-arrow' />
                                            </span>
                                        </td>
                                        <td className="desktop-only">{user.email}</td>
                                        <td className="desktop-only">{user.username}</td>
                                        <td className="desktop-only">{user.organization}</td>
                                        <td className="desktop-only">{user.access}</td>
                                    </tr>

                                    {expandedIndices.includes(index) && (
                                        <tr className="mobile-only expanded-row">
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5rem' }}><strong>Email</strong> {user.email}</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5rem' }}><strong>Username</strong> {user.username}</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5rem' }}><strong>Organization</strong> {user.organization}</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5rem' }}><strong>Access</strong> {user.access}</div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>

                    </table>
                </div>
            )}


        </div>
    )
}

export default ClientEsriCredentials